import React, { FC, lazy, Suspense, useEffect, useMemo } from "react";
import {
  check2faRequired,
  checkWalletCreated,
} from "@/lib/helpers/stytch-validation";
import { useStytch, useStytchSession, useStytchUser } from "@stytch/react";

import Login from "@/lib/login";
import { useWidget } from "@/lib/hooks/use-widget";
import { Skeleton } from "@/lib/components/ui/skeleton";
import { useLogin } from "@/lib/hooks/use-login";

const UserButton = lazy(() => import("@/lib/profile/user-button"));

interface Props {
  noRedirect?: boolean;
}

const Main: FC<Props> = ({ noRedirect = false }) => {
  const { session } = useStytchSession();
  const { user } = useStytchUser();
  const stytch = useStytch();
  const { startLogin } = useLogin();
  const { addresses, isLoading } = useWidget();
  const hasAccount = Object.keys(addresses).length > 0;

  const isValidSession = useMemo(() => {
    const isWalletCreated = checkWalletCreated(user?.trusted_metadata || {});
    const is2FARequired = check2faRequired(
      session?.authentication_factors || [],
    );

    return isWalletCreated && !is2FARequired;
  }, [session?.authentication_factors, user?.trusted_metadata]);

  useEffect(() => {
    if (!startLogin || !window.MPCWidget) return;
    window.MPCWidget.login = startLogin;
    window.MPCWidget.hasAccount = hasAccount;
    window.MPCWidget.isLoading = isLoading;
    window.MPCWidget.user = user;
  }, [startLogin, hasAccount, isLoading, user]);

  useEffect(() => {
    const refresh = () => {
      if (stytch.session.getSync()) {
        stytch.session.authenticate({
          session_duration_minutes: 60,
        });
      }
    };

    refresh();
    // Refresh session every 50 minutes
    const interval = setInterval(refresh, 3000000);
    return () => clearInterval(interval);
  }, [stytch]);

  return (
    <Suspense>
      {isValidSession ? <UserButton /> : <Login noRedirect={noRedirect} />}
    </Suspense>
  );
};

export const MPCWidget: FC<Props> = ({ noRedirect }) => {
  const { stytch } = useWidget();

  if (stytch) {
    return <Main noRedirect={noRedirect} />;
  }

  return <Skeleton className="h-8 w-8 rounted-full" />;
};
